import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ChevronDown, ChevronLeft, ChevronRight, Star } from "lucide-react";
import { useLang } from "@/i18n";
import { useBooking } from "@/BookingContext";
import Reveal from "@/components/Reveal";

const BLOCKS = [
  { key: "gardiennage", imgKey: "door" },
  { key: "linge", imgKey: "linen" },
  { key: "bateau", imgKey: "boat" },
  { key: "courses", imgKey: "groceries" },
];

const REVIEWS = [
  {
    name: "Famille Bernard",
    fr: "Un séjour inoubliable en Corse-du-Sud. La maison était parfaitement préparée et l'équipe de Présence Home Services aux petits soins, du premier échange jusqu'au départ.",
    en: "An unforgettable stay in South Corsica. The house was perfectly prepared and the Présence Home Services team took care of everything, from the first exchange to departure.",
  },
  {
    name: "Marie L.",
    fr: "Conciergerie très professionnelle : linge impeccable, courses livrées avant notre arrivée et une sortie en bateau organisée au départ de Porto-Vecchio. Parfait.",
    en: "Very professional concierge service: impeccable linen, groceries delivered before our arrival and a boat trip organised from Porto-Vecchio. Perfect.",
  },
  {
    name: "J. Pietri",
    fr: "Nous confions notre villa à Présence Home Services depuis deux ans : un suivi sérieux, des rapports après chaque passage et une vraie tranquillité pour nous.",
    en: "We have entrusted our villa to Présence Home Services for two years: serious follow-up, reports after each visit and real peace of mind for us.",
  },
  {
    name: "Sophie & Marc D.",
    fr: "Massage à domicile, chef privé, chauffeur pour nos soirées : tout était organisé à la perfection. Nous reviendrons, c'est certain.",
    en: "Massage at home, private chef, driver for our evenings: everything was organised to perfection. We will definitely be back.",
  },
  {
    name: "Caroline R.",
    fr: "Une adresse rare : des maisons magnifiques, une équipe locale disponible et discrète, et cette impression d'être en vacances comme chez soi.",
    en: "A rare find: beautiful houses, an available and discreet local team, and that feeling of being on holiday while feeling at home.",
  },
];

function Stars() {
  return (
    <div className="flex gap-1">
      {[0, 1, 2, 3, 4].map((i) => (
        <Star key={i} size={13} strokeWidth={1} className="fill-[#B08D57] text-[#B08D57]" />
      ))}
    </div>
  );
}

function ReviewsSection() {
  const { t, lang } = useLang();
  const [start, setStart] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => setStart((s) => (s + 1) % REVIEWS.length), 6000);
    return () => clearInterval(id);
  }, [paused]);

  const visible = [0, 1, 2].map((i) => REVIEWS[(start + i) % REVIEWS.length]);

  return (
    <section data-testid="reviews-section" className="bg-ivoire">
      <div
        className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-28"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <div className="flex flex-wrap items-end justify-between gap-6">
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-maquis-deep">{t.services.avis.title}</h2>
          <div className="flex items-center gap-3" data-testid="reviews-rating">
            <Stars />
            <span className="text-sm text-maquis-deep">{t.services.avis.rating}</span>
            <span className="text-xs text-maquis-soft">· {t.services.avis.source}</span>
          </div>
        </div>

        <div className="mt-12 grid gap-5 md:grid-cols-3" key={start}>
          {visible.map((r, i) => (
            <figure
              key={r.name}
              data-testid={`review-card-${i}`}
              className="flex animate-fade-up flex-col rounded-md border border-maquis-deep/10 bg-casse p-7 sm:p-9"
            >
              <figcaption className="text-sm font-medium text-maquis-deep">{r.name}</figcaption>
              <div className="mt-2"><Stars /></div>
              <blockquote className="mt-5 flex-1 font-serif text-lg leading-relaxed text-maquis-olive">
                “{lang === "en" ? r.en : r.fr}”
              </blockquote>
              <p className="mt-6 text-right text-xs text-maquis-soft">{t.services.avis.source}</p>
            </figure>
          ))}
        </div>

        <div className="mt-10 flex items-center justify-center gap-6">
          <button
            data-testid="reviews-prev"
            onClick={() => setStart((s) => (s - 1 + REVIEWS.length) % REVIEWS.length)}
            className="rounded-full border border-maquis-deep/20 p-3 text-maquis-deep transition-colors hover:bg-casse"
            aria-label="Avis précédent"
          >
            <ChevronLeft size={18} strokeWidth={1.5} />
          </button>
          <div className="flex gap-2">
            {REVIEWS.map((_, i) => (
              <span key={i} className={`h-1 rounded-full transition-all duration-300 ${i === start ? "w-8 bg-[#B08D57]" : "w-3 bg-sable"}`} />
            ))}
          </div>
          <button
            data-testid="reviews-next"
            onClick={() => setStart((s) => (s + 1) % REVIEWS.length)}
            className="rounded-full border border-maquis-deep/20 p-3 text-maquis-deep transition-colors hover:bg-casse"
            aria-label="Avis suivant"
          >
            <ChevronRight size={18} strokeWidth={1.5} />
          </button>
        </div>
      </div>
    </section>
  );
}

export default function ServicesPage() {
  const { t, simg } = useLang();
  const { openBooking } = useBooking();
  const location = useLocation();
  const [openKey, setOpenKey] = useState(null);

  useEffect(() => {
    if (location.hash) {
      const key = location.hash.slice(1);
      if (BLOCKS.some((b) => b.key === key)) setOpenKey(key);
      const el = document.querySelector(location.hash);
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 200);
    }
  }, [location.hash]);

  return (
    <main data-testid="services-page">
      <header className="relative flex h-[55vh] min-h-[420px] items-end overflow-hidden">
        <img src={simg("coastRugged")} alt="Côte de Corse-du-Sud" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-mer/80 via-mer/30 to-mer/40" />
        <div className="relative mx-auto w-full max-w-7xl px-5 sm:px-8 pb-14 text-casse">
          <p className="eyebrow text-sable">{t.services.eyebrow}</p>
          <h1 className="mt-4 font-serif text-4xl sm:text-5xl lg:text-6xl">{t.services.title}</h1>
          <p className="mt-4 text-justify text-sm sm:text-base text-casse/85">{t.services.sub}</p>
        </div>
      </header>

      <div className="mx-auto max-w-5xl px-5 sm:px-8 py-10 sm:py-14">
        {BLOCKS.map(({ key, imgKey }, i) => {
          const open = openKey === key;
          return (
            <section key={key} id={key} data-testid={`service-block-${key}`} className="border-b border-maquis-deep/10 last:border-b-0">
              <button
                data-testid={`service-accordion-${key}`}
                onClick={() => setOpenKey(open ? null : key)}
                className="flex w-full items-center justify-between gap-6 py-8 sm:py-10 text-left"
              >
                <div className="flex items-baseline gap-5 sm:gap-8">
                  <span className="eyebrow">{String(i + 1).padStart(2, "0")}</span>
                  <h2 className="font-serif text-2xl sm:text-4xl text-maquis-deep">{t.services.items[key].title}</h2>
                </div>
                <ChevronDown
                  size={22}
                  strokeWidth={1.25}
                  className={`shrink-0 text-maquis-olive transition-transform duration-300 ${open ? "rotate-180" : ""}`}
                />
              </button>
              <div className={`grid transition-all duration-500 ease-out ${open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                <div className="overflow-hidden">
                  <div className="grid items-center gap-8 pb-12 lg:grid-cols-2 lg:gap-16">
                    <div className="overflow-hidden rounded-md">
                      <img
                        src={simg(imgKey)}
                        alt={t.services.items[key].title}
                        loading="lazy"
                        className="h-64 w-full object-cover sm:h-[340px] transition-transform duration-[1.4s] ease-out hover:scale-105"
                      />
                    </div>
                    <div>
                      <p className="text-base leading-relaxed text-maquis-olive">{t.services.items[key].desc}</p>
                      <p className="mt-5 text-xs uppercase tracking-[0.18em] text-maquis-soft">{t.services.items[key].info}</p>
                      <button
                        data-testid={`service-cta-${key}`}
                        onClick={() => openBooking({ services: [key] })}
                        className="btn-outline-elegant mt-7 text-maquis-deep hover:bg-maquis-deep hover:text-casse"
                      >
                        {t.services.cta}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          );
        })}
      </div>

      <ReviewsSection />
    </main>
  );
}
